/**
 * @file client.ts
 * @brief Axios HTTP client preconfigured for the Verblizr backend.
 *
 * @details
 * - Adds `Authorization: Bearer <token>` from `tokenStorage` to every
 *   outgoing request.
 * - Normalizes errors into a typed `ApiClientError` with `status`, `code`,
 *   and `message` fields so feature code can do clean `if/else` logic
 *   instead of digging through Axios internals.
 * - On a 401 that carries our own `UNAUTHORIZED` code it broadcasts a custom
 *   `verblizr:auth:expired` event. The `AuthContext` listens for it and
 *   triggers a global logout. We deliberately keep this transport-layer
 *   concern here so circular imports don't form. Keying on the CODE rather
 *   than the bare status matters — see the note beside the interceptor.
 *
 * @dependencies
 * - axios
 * - shared/api/tokenStorage : JWT persistence
 * - shared/config/env       : base URL configuration
 *
 * @references
 * - BACKEND/src/shared/middleware/auth.middleware.js (token verifier)
 * - BACKEND/src/app.js (CORS — both `/api/*` and short-form routes mounted)
 *
 * @author DES
 * @date 26-05-2026
 */

import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from "axios";
import { env } from "@/shared/config/env";
import { tokenStorage } from "./tokenStorage";

// ----------------------------------------------------------------------
// Custom error type
// Feature code should `instanceof ApiClientError` rather than poking at
// Axios shapes. Keeping a stable contract here means we can swap the
// underlying transport (fetch / ky / etc.) without touching callers.
// ----------------------------------------------------------------------
export class ApiClientError extends Error {
  public readonly status: number;
  public readonly code: string | undefined;
  public readonly raw: unknown;

  constructor(message: string, status: number, code?: string, raw?: unknown) {
    super(message);
    this.name = "ApiClientError";
    this.status = status;
    this.code = code;
    this.raw = raw;
  }
}

export const AUTH_EXPIRED_EVENT = "verblizr:auth:expired";

const DEVICE_ID_KEY = "verblizr.webpanel.deviceId";
let cachedDeviceId: string | null = null;

/** A random id kept in this browser (or Mac app) for good; null if storage is blocked. */
export function stableDeviceId(): string | null {
  if (cachedDeviceId) return cachedDeviceId;
  if (typeof window === "undefined") return null;
  try {
    let id = window.localStorage.getItem(DEVICE_ID_KEY);
    if (!id) {
      id =
        typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : `web-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
      window.localStorage.setItem(DEVICE_ID_KEY, id);
    }
    cachedDeviceId = id;
    return id;
  } catch {
    return null;
  }
}

// ----------------------------------------------------------------------
// Axios instance
// `withCredentials` is left at default (false) because we use Bearer
// tokens, not cookies. Switch to `true` if/when we move the JWT into an
// httpOnly cookie.
// ----------------------------------------------------------------------
export const apiClient: AxiosInstance = axios.create({
  baseURL: env.apiBaseUrl,
  timeout: 30_000,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

// B43 (31-10-2026, ADR-061): the Mac app's free-minutes device hash, read
// once from the desktop shell (the web sends none: no device id, no
// fingerprinting; web relies on the email checks). Undefined = not read yet.
let freeDeviceId: string | null | undefined;
async function desktopFreeDeviceId(): Promise<string | null> {
  if (freeDeviceId !== undefined) return freeDeviceId;
  try {
    const shell = typeof window !== "undefined"
      ? (window as unknown as { verblizrDesktop?: { getFreeDeviceId?: () => Promise<string | null> } }).verblizrDesktop
      : undefined;
    const id = shell?.getFreeDeviceId ? await shell.getFreeDeviceId() : null;
    freeDeviceId = typeof id === "string" && /^[0-9a-f]{64}$/.test(id) ? id : null;
  } catch {
    freeDeviceId = null;
  }
  return freeDeviceId;
}

// ----------------------------------------------------------------------
// Request interceptor — attach JWT
// ----------------------------------------------------------------------
apiClient.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  const token = tokenStorage.get();
  config.headers = config.headers ?? {};
  // A call that names its own bearer keeps it: the 2FA setup-only screen
  // sends the setup token (B31), even when another session is stored here.
  const h = config.headers as { has?: (name: string) => boolean } & Record<string, unknown>;
  const explicitAuth = typeof h.has === "function" ? h.has("Authorization") : Boolean(h["Authorization"]);
  if (token && !explicitAuth) {
    (config.headers as Record<string, string>)["Authorization"] = `Bearer ${token}`;
  }
  // Which app is calling (BACKEND store safety, 18-10-2026): `desktop` inside
  // the Mac app, `web` otherwise. Web and desktop get the same answers today;
  // the phone sends ios / android. Every call already preflights, so this adds
  // no CORS round trip.
  // One stable id per browser / Mac install (one account = one person,
  // ADR-045): the server tells two devices apart for ACCOUNT_BUSY and the
  // signed-in devices list. Random, stored locally, never personal data.
  const deviceId = stableDeviceId();
  if (deviceId) (config.headers as Record<string, string>)["x-device-id"] = deviceId;
  (config.headers as Record<string, string>)["x-client-surface"] =
    typeof window !== "undefined" && (window as unknown as { verblizrDesktop?: unknown }).verblizrDesktop
      ? "desktop"
      : "web";
  // "This build can ask How VERBLIZR uses AI" (BACKEND B7, 22-10-2026): the
  // server's AI gate refuses only clients that send it, so old builds keep working.
  (config.headers as Record<string, string>)["x-ai-consent"] = "1";
  const freeId = await desktopFreeDeviceId();
  if (freeId) {
    (config.headers as Record<string, string>)["x-free-device-platform"] = "mac";
    (config.headers as Record<string, string>)["x-free-device-id"] = freeId;
  }
  return config;
});

// ----------------------------------------------------------------------
// AI agreement (B7): a 403 AI_CONSENT_REQUIRED shows the sheet (registered
// by features/ai-consent/consentBridge, so this file imports no UI) and, on
// "Agree", sends the same call again once. "Not now" rejects as usual.
// ----------------------------------------------------------------------
type AiConsentHandler = (body: unknown) => Promise<boolean>;
let aiConsentHandler: AiConsentHandler | null = null;
export function setAiConsentHandler(handler: AiConsentHandler | null) {
  aiConsentHandler = handler;
}
type RetryConfig = InternalAxiosRequestConfig & { _aiConsentRetried?: boolean };

// ----------------------------------------------------------------------
// Response interceptor — error normalization + 401 broadcast
// ----------------------------------------------------------------------
apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError<{ message?: string; code?: string; error?: string }>) => {
    const status = error.response?.status ?? 0;
    const data = error.response?.data;
    const message =
      data?.message ||
      data?.error ||
      error.message ||
      "Unexpected network error";
    // Backend often returns `error` (e.g. MISSING_PAYMENT_METHOD on 402)
    // rather than `code` — accept both.
    const code = data?.code || data?.error;

    const sent = error.config as RetryConfig | undefined;
    if (status === 403 && code === "AI_CONSENT_REQUIRED" && aiConsentHandler && sent && !sent._aiConsentRetried) {
      if (await aiConsentHandler(data)) {
        return apiClient({ ...sent, _aiConsentRetried: true } as RetryConfig);
      }
    }

    // ------------------------------------------------------------------
    // Log out on OUR 401s, not on everybody's (05-08-2026)
    //
    // This used to fire on any 401. Then the Asana connector's access token
    // expired, Asana answered 401, that status travelled up through the API,
    // and the panel logged the user out of Verblizr — repeatedly, for a
    // failure in a completely different system. Every future integration
    // carries the same hazard.
    //
    // Our auth layer is the only thing that emits `UNAUTHORIZED` (the auth
    // middleware and requireFeature); a failed login sends
    // `INVALID_CREDENTIALS`, which is not a dying session and must not clear
    // one. Anything else with a 401 came from somewhere that is not our
    // session, and the server maps those to their own codes.
    //
    // Being strict here is safe: leaving a stale token in storage grants
    // nobody anything, because the server is the authority and rejects it
    // regardless. A wrongful logout, by contrast, costs the user their work.
    // ------------------------------------------------------------------
    if (status === 401 && code === "UNAUTHORIZED") {
      tokenStorage.clear();
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT));
      }
    }

    return Promise.reject(new ApiClientError(message, status, code, error));
  },
);

// ----------------------------------------------------------------------
// Convenience helpers (typed wrappers around common verbs)
// Useful for endpoints where the response shape is already known.
// ----------------------------------------------------------------------
export async function apiGet<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const res = await apiClient.get<T>(url, config);
  return res.data;
}

export async function apiPost<T, B = unknown>(
  url: string,
  body?: B,
  config?: AxiosRequestConfig,
): Promise<T> {
  const res = await apiClient.post<T>(url, body, config);
  return res.data;
}

export async function apiPut<T, B = unknown>(
  url: string,
  body?: B,
  config?: AxiosRequestConfig,
): Promise<T> {
  const res = await apiClient.put<T>(url, body, config);
  return res.data;
}

export async function apiPatch<T, B = unknown>(
  url: string,
  body?: B,
  config?: AxiosRequestConfig,
): Promise<T> {
  const res = await apiClient.patch<T>(url, body, config);
  return res.data;
}

export async function apiDelete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const res = await apiClient.delete<T>(url, config);
  return res.data;
}
